const BASE = '/admin/application/api'

export interface AdminUserSummary {
  id: string
  email: string
  name: string
  admin: boolean
}

export interface AdminUser {
  id: string
  email: string
  name: string
  admin: boolean
  workspaces: { id: string; business_name: string; owned: boolean }[]
}

export interface AdminPeppolIdentifier {
  id: string
  scheme: string
  endpoint_id: string
  peppol_id: string
}

export interface AdminWorkspace {
  id: string
  business_name: string
  vat_number: string | null
  peppol_status: string | null
  peppol_identifiers: AdminPeppolIdentifier[]
}

export class AdminApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

async function request<T>(method: string, path: string, body?: object): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  })
  const json = await response.json().catch(() => ({}))

  if (!response.ok) throw new AdminApiError(json.error || response.statusText, response.status)

  return json as T
}

export const AdminController = {
  searchUsers: (query: string) => request<AdminUserSummary[]>('GET', `/users?query=${encodeURIComponent(query)}`),
  getUser: (id: string) => request<AdminUser>('GET', `/users/${encodeURIComponent(id)}`),
  createSignInLink: (id: string) =>
    request<{ url: string; expires_at: string }>('POST', `/users/${encodeURIComponent(id)}/sign_in_link`),
  deleteUser: (id: string, emailConfirmation: string) =>
    request<{ queued: boolean }>('DELETE', `/users/${encodeURIComponent(id)}`, { email_confirmation: emailConfirmation }),
  getWorkspace: (id: string) => request<AdminWorkspace>('GET', `/workspaces/${encodeURIComponent(id)}`),
  deactivatePeppol: (id: string) =>
    request<{ workspace: AdminWorkspace; warning: string | null }>('DELETE', `/workspaces/${encodeURIComponent(id)}/peppol`),
}
